import { IDialogInfo } from '../../../store/Dialog';
import { AddOrganizationContextData as IAddOrgState } from '../../../Hooks/addOrganization';
import {
  ipPatt,
  fqdnPatt,
  channelNamePatt,
} from '../../../utils/regexPatterns';

// True if the string is invalid
const isStrInvalid = (value: string) =>
  !value || value === '' || value.indexOf(' ') >= 0;

const hasDuplicatedElements = (
  array: any[],
  key = '',
): { idx1: number; idx2: number } | false => {
  const uniques: string[] = [];

  // using a for loop because I need to return on the function context
  for (let i = 0; i < array.length; i++) {
    const el = array[i];
    const arrayEl = typeof el === 'object' ? el[key] : el;

    const index = uniques.findIndex((element) =>
      typeof element === 'object'
        ? element[key] === arrayEl
        : element === arrayEl,
    );

    if (index >= 0) return { idx1: index, idx2: i };

    uniques.push(arrayEl);
  }

  return false;
};

export const validateChannels = (
  channels: Channel[],
  openDialog: (p: IDialogInfo) => void,
  t: (a: string, b?: object) => string,
) => {
  const duplicatedChannel = hasDuplicatedElements(channels, 'channelName');
  const invalidChannelName = channels.some(
    (mappedChannel) => !mappedChannel.channelName,
  );

  if (invalidChannelName) {
    openDialog({
      title: 'Invalid channel name',
      type: 'error',
      content: 'Some of the channel names are invalid',
    });
    return false;
  }
  if (duplicatedChannel) {
    const { idx1, idx2 } = duplicatedChannel;
    const { channelName } = channels[idx1];
    const channelNameToDelete = channels[idx2].channelName;

    openDialog({
      title: t('deploy.validation.channel.duplicated'),
      type: 'error',
      content: t('deploy.validation.channel.duplicated.content', {
        channelName,
        channelNameToDelete,
      }),
    });

    return false;
  }

  // channels' peers must not be empty
  const emptyPeers = channels.find(
    (channel) => Object?.keys(channel?.peers)?.length === 0,
  );
  if (emptyPeers) {
    const { channelName } = emptyPeers;

    openDialog({
      title: t('deploy.validation.channel.emptyPeers'),
      type: 'error',
      content: t('deploy.validation.channel.emptyPeers.content', {
        channelName,
      }),
    });
    return false;
  }

  return true;
};

// organizations definitions checking
export const ValidateOrgs = (
  addOrganization: IAddOrgState,
  openDialog: (p: IDialogInfo) => void,
  t: (a: string, b?: object) => string,
) => {
  try {
    // Checking for duplicated CA IPs
    let idxs = hasDuplicatedElements(addOrganization.orgs, 'caIP');

    if (idxs !== false) {
      throw Error(
        t('asset.network.deploy.messages.orgsWithSameNetworkAPI', {
          org1: addOrganization.orgs[idxs.idx1].orgName,
          org2: addOrganization.orgs[idxs.idx2].orgName,
        }),
      );
    }

    // Checking for duplicated peers, orderers, ccapis
    const ips: string[] = [];

    addOrganization.orgs.forEach((org) => {
      org.peers.forEach((peer) => {
        ips.push(peer.value);
      });
    });

    idxs = hasDuplicatedElements(ips);

    if (idxs !== false) {
      throw Error(t('asset.network.deploy.messages.duplicatedNodes'));
    }

    // Org checking
    const checkOrgs = addOrganization.orgs.map((org: IOrg) => {
      const { caIP, user, passwd, orgName, orgDomainName, confirmPasswd } = org;

      const isOrgInvalid =
        isStrInvalid(user) ||
        isStrInvalid(passwd) ||
        isStrInvalid(caIP) ||
        passwd !== confirmPasswd ||
        !(ipPatt.test(caIP) || fqdnPatt.test(caIP)) ||
        isStrInvalid(orgName) ||
        orgName.indexOf(' ') >= 0 ||
        isStrInvalid(orgDomainName) ||
        orgDomainName.indexOf(' ') >= 0;
      if (isOrgInvalid) return false;
      return true;
    });
    if (checkOrgs.includes(false))
      throw Error(t('asset.network.deploy.messages.orgNonFilledProperly'));

    if (addOrganization.orgs.length === 0) {
      throw Error(t('asset.network.deploy.messages.noFilledOrgs'));
    }

    const checkedOrgsPeers = addOrganization.orgs.map(
      (org) => org.peers.length !== 0,
    );
    if (checkedOrgsPeers.includes(false))
      throw Error(t('asset.network.deploy.messages.orgsWithoutPeers'));

    const checkForPeers: Record<string, boolean[]> = {};
    const checkForOrderers: Record<string, boolean[]> = {};

    addOrganization.orgs.forEach((org: IOrg) => {
      checkForOrderers[org.orgName] = [];
      checkForPeers[org.orgName] = [];

      org.peers.forEach((peer) => {
        const valid: boolean =
          (peer.value !== '' && peer.valid && ipPatt.test(peer.value)) ||
          fqdnPatt.test(peer.value);

        checkForPeers[org.orgName].push(valid && peer.opts.peer);
        checkForOrderers[org.orgName].push(valid && peer.opts.orderer);
      });
    });

    // Check if there's at least one orderer and one peer by org
    const checkedPeers: Record<string, boolean> = {};
    const checkedOrderers: Record<string, boolean> = {};
    addOrganization.orgs.forEach((org) => {
      checkedOrderers[org.orgName] = checkForOrderers[org.orgName].includes(
        true,
      );
      checkedPeers[org.orgName] = checkForPeers[org.orgName].includes(true);
    });

    addOrganization.orgs.forEach((org) => {
      if (!checkedPeers[org.orgName]) {
        throw Error(
          t('asset.network.deploy.messages.atLeastOnePeer', {
            orgName: org.orgName,
          }),
        );
      }

      if (!checkedOrderers[org.orgName]) {
        throw Error(
          t('asset.network.deploy.messages.atLeastOneOrderer', {
            orgName: org.orgName,
          }),
        );
      }
    });
  } catch (error) {
    openDialog({
      title: t('common.words.error'),
      type: 'error',
      content: error.message,
    });
    return false;
  }

  return true;
};
